import React, { useEffect, useRef } from "react";

// Canvas "grainient" — mesh gradient with film-grain noise.
// The gradient's control points orbit slowly and the colors are re-blended
// every frame, so the color field morphs like fluid; the noise layer is
// generated once and composited on top, so grain stays put like film.
function mulberry32(a: number) {
  return function () {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** One control point of the mesh: position in 0..1 coords, and its colour. */
interface GrainientPoint {
  x: number;
  y: number;
  c: [number, number, number];
}

// points: [{ x, y, c: [r, g, b] }] in 0..1 coords.
// flow scales orbit amplitude per axis — e.g. [1.8, 0.3] on a wide short
// button makes the colors slosh along its length instead of vertically.
export default function Grainient({ points, seed = 7, noise = 22, flow = [1, 1], className = "" }: {
  points: GrainientPoint[];
  seed?: number;
  noise?: number;
  flow?: [number, number];
  className?: string;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const w = (canvas.width = Math.max(2, Math.round(canvas.offsetWidth)));
    const h = (canvas.height = Math.max(2, Math.round(canvas.offsetHeight)));
    const ctx = canvas.getContext("2d")!;
    const rand = mulberry32(seed);

    // Static full-res grain, mid-gray so 'overlay' compositing only modulates
    const nc = document.createElement("canvas");
    nc.width = w; nc.height = h;
    const nctx = nc.getContext("2d")!;
    const nimg = nctx.createImageData(w, h);
    for (let i = 0; i < nimg.data.length; i += 4) {
      const v = 128 + (rand() - 0.5) * 2 * noise;
      nimg.data[i] = nimg.data[i + 1] = nimg.data[i + 2] = v;
      nimg.data[i + 3] = 255;
    }
    nctx.putImageData(nimg, 0, 0);

    // Low-res buffer for the color field — upscaled with smoothing, so the
    // per-frame IDW loop stays tiny (~7k px) while blobs render soft
    const gw = 72, gh = Math.max(2, Math.round((72 * h) / w));
    const gc = document.createElement("canvas");
    gc.width = gw; gc.height = gh;
    const gctx = gc.getContext("2d")!;
    const gimg = gctx.createImageData(gw, gh);

    // Per-point orbit params, seeded so the motion is stable across mounts
    const orbits = points.map(() => ({
      ax: (0.08 + rand() * 0.08) * flow[0],
      ay: (0.08 + rand() * 0.08) * flow[1],
      sx: 0.12 + rand() * 0.18,
      sy: 0.12 + rand() * 0.18,
      px: rand() * Math.PI * 2,
      py: rand() * Math.PI * 2,
    }));

    const drawFrame = (t: number) => {
      const d = gimg.data;
      let i = 0;
      for (let y = 0; y < gh; y++) {
        const ny = y / gh;
        for (let x = 0; x < gw; x++) {
          const nx = x / gw;
          let r = 0, g = 0, b = 0, tw = 0;
          for (let k = 0; k < points.length; k++) {
            const o = orbits[k];
            const dx = nx - (points[k].x + o.ax * Math.sin(t * o.sx + o.px));
            const dy = ny - (points[k].y + o.ay * Math.cos(t * o.sy + o.py));
            const wgt = 1 / (Math.pow(dx * dx + dy * dy, 1.3) + 1e-4);
            r += points[k].c[0] * wgt; g += points[k].c[1] * wgt; b += points[k].c[2] * wgt;
            tw += wgt;
          }
          d[i++] = r / tw; d[i++] = g / tw; d[i++] = b / tw; d[i++] = 255;
        }
      }
      gctx.putImageData(gimg, 0, 0);
      ctx.globalCompositeOperation = "source-over";
      ctx.drawImage(gc, 0, 0, w, h);
      ctx.globalCompositeOperation = "overlay";
      ctx.drawImage(nc, 0, 0);
      ctx.globalCompositeOperation = "source-over";
    };

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      drawFrame(0);
      return;
    }
    let raf: number;
    const loop = (ms: number) => {
      drawFrame(ms / 1000);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points, seed, noise, flow[0], flow[1]]);
  return <canvas ref={ref} className={className} aria-hidden="true" />;
}
